// states
import { createSignal, onMount } from "solid-js";

// PWA Service Worker Registration
export function registerServiceWorker() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker
      .register(`/${__APP_NAME__}/sw.js`)
      .then((registration) => {
        window.swRegistration = registration;
        console.log("SW registered: ", registration);
      })
      .catch((registrationError) => {
        console.log("SW registration failed: ", registrationError);
      });
  }
}

// 检查是否支持PWA安装
export function isPwaSupported(): boolean {
  return "serviceWorker" in navigator && "BeforeInstallPromptEvent" in window;
}

// 检查是否已经安装
export function isPwaInstalled(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone ===
      true
  );
}

type BeforeInstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

// 全局PWA状态管理
export const pwaState = {
  deferredPrompt: createSignal<BeforeInstallPromptEvent | null>(null),
  isInstallable: createSignal(false),
  isInstalled: createSignal(false),
  isInstalling: createSignal(false),
  installProgress: createSignal(0),
  installComplete: createSignal(false),
  userChoice: createSignal<"pending" | "accepted" | "dismissed">("pending"),
};

// 获取setter函数的辅助函数
const getSetters = () => ({
  setDeferredPrompt: pwaState.deferredPrompt[1],
  setIsInstallable: pwaState.isInstallable[1],
  setIsInstalled: pwaState.isInstalled[1],
  setIsInstalling: pwaState.isInstalling[1],
  setInstallProgress: pwaState.installProgress[1],
  setInstallComplete: pwaState.installComplete[1],
  setUserChoice: pwaState.userChoice[1],
});

// 初始化PWA状态
export function initPwaState() {
  onMount(() => {
    const setters = getSetters();

    // 检查是否已经安装
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setters.setIsInstalled(true);
      return;
    }

    // 检查是否支持PWA
    if (!("serviceWorker" in navigator)) return;

    // 监听 beforeinstallprompt 事件
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setters.setDeferredPrompt(promptEvent);
      setters.setIsInstallable(true);
    };

    // 监听 appinstalled 事件
    const handleAppInstalled = () => {
      setters.setIsInstalled(true);
      setters.setIsInstallable(false);
      setters.setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
  });
}

// PWA安装函数 - 显示安装提示并等待用户选择
export const installPwa = async (): Promise<boolean> => {
  const [deferredPrompt] = pwaState.deferredPrompt;
  const setters = getSetters();

  const prompt = deferredPrompt();
  if (!prompt) {
    return false;
  }

  try {
    // 显示安装提示
    await prompt.prompt();

    // 等待用户选择
    const choiceResult = await prompt.userChoice;

    if (choiceResult.outcome === "accepted") {
      setters.setUserChoice("accepted");
      setters.setIsInstalled(true);
      setters.setIsInstallable(false);
      setters.setDeferredPrompt(null);
      return true;
    } else {
      setters.setUserChoice("dismissed");
      return false;
    }
  } catch (error) {
    setters.setUserChoice("dismissed");
    return false;
  }
};

// 开始安装进度条
export const startInstallProgress = () => {
  const setters = getSetters();

  setters.setIsInstalling(true);
  setters.setInstallProgress(0);
  setters.setInstallComplete(false);

  let progress = 0;
  const interval = setInterval(() => {
    progress += 2; // 每40ms增加2%，总共2秒完成
    setters.setInstallProgress(progress);

    if (progress >= 100) {
      clearInterval(interval);
      setters.setIsInstalling(false);
      setters.setInstallComplete(true);
    }
  }, 80); // 80ms * 50次 = 4秒
};

// 重置安装状态
export const resetInstallState = () => {
  const setters = getSetters();

  setters.setIsInstalling(false);
  setters.setInstallProgress(0);
  setters.setInstallComplete(false);
  setters.setUserChoice("pending");
};
